import "fixed-data-table-2/dist/fixed-data-table.css";

import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Table, Column, Cell, ColumnGroup } from "fixed-data-table-2";
import { getSoloPhysNum } from "util/solo";

const TIME_DIFF_WARN_S = 5 * 60;
const TIME_DIFF_ERR_S = 8 * 60;
const DEPTH_DIFF_WARN_M = 5;
const DEPTH_DIFF_ERR_M = 10;

const styles = {
  textInputStyle: {
    width: "100%",
    background: "transparent",
    border: "none"
  },
  warnDark: {
    backgroundColor: "hsl(40, 100%, 89%)"
  },
  warnLight: {
    backgroundColor: "hsl(40, 100%, 93%)"
  },
  errorDark: {
    backgroundColor: "hsl(359, 87%, 89%)"
  },
  errorLight: {
    backgroundColor: "hsl(359, 87%, 93%)"
  },
  groupHeaderStyle: {
    fontWeight: "bold",
    marginTop: -4
  },
  headerStyle: {
    fontWeight: "bold",
    marginTop: 5
  },
  tableFont: {
    fontFamily: "Roboto",
    fontSize: "14px"
  },
  duplicateDark: {
    backgroundColor: "#ffa061",
    color: "white"
  },
  duplicateLight: {
    backgroundColor: "#ffe1cc"
  }
};

const getTimeCellStyle = (castTime, formTime, rowIndex) => {
  const timeDeltaMs = Math.abs(moment(castTime).diff(moment(formTime)));
  if (timeDeltaMs > TIME_DIFF_ERR_S * 1000) {
    return rowIndex % 2 === 0 ? styles.errorLight : styles.errorDark;
  }
  if (timeDeltaMs > TIME_DIFF_WARN_S * 1000) {
    return rowIndex % 2 === 0 ? styles.warnLight : styles.warnDark;
  }
  return {};
};

const getDepthCellStyle = (castDepth, formDepth, rowNum) => {
  const depthDelta = Math.abs(formDepth - castDepth);
  if (depthDelta > DEPTH_DIFF_ERR_M) {
    return rowNum % 2 === 0 ? styles.errorLight : styles.errorDark;
  }
  if (depthDelta > DEPTH_DIFF_WARN_M) {
    return rowNum % 2 === 0 ? styles.warnLight : styles.warnDark;
  }
  return {};
};

const getFormDuplicatesStyle = (soloCastPk, duplicates, rowNum) => {
  if (duplicates.includes(soloCastPk))
    return rowNum % 2 === 0 ? styles.duplicateLight : styles.duplicateDark;
  return {};
};

const CastsInFileCard = props => {
  if (props.soloCastsInFilesBeingAnnotatedList.length > 0) {
    return (
      <div style={styles.tableFont}>
        <Table
          rowsCount={props.soloCastsInFilesBeingAnnotatedList.length}
          rowHeight={30}
          headerHeight={30}
          groupHeaderHeight={30}
          width={props.width - 5}
          height={
            (props.soloCastsInFilesBeingAnnotatedList.length + 2) * 30 + 17
          }
        >
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Change</Cell>}
            fixed
          >
            <Column
              header="form"
              fixed
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const possibleForms = props.possibleForms[soloData.soloCastPk];
                const style = getFormDuplicatesStyle(
                  soloData.soloCastPk,
                  props.soloDuplicateFormsInCastsBeingAnnotated,
                  rowIndex
                );
                // If there are no matches, make the button say so
                if (
                  !possibleForms ||
                  (possibleForms.length === 1 && !possibleForms[0].date)
                ) {
                  return (
                    <Cell {...colProps}>
                      <input type="button" value="no match" />
                    </Cell>
                  );
                }
                return (
                  <Cell {...colProps} style={style}>
                    <input
                      type="button"
                      value={`${possibleForms.length} matches`}
                      onClick={() => props.onCastChangeFormClick(soloData)}
                      style={style}
                    />
                  </Cell>
                );
              }}
              width={100}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Cast</Cell>}
            fixed
          >
            <Column
              header="data"
              fixed
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                return (
                  <Cell {...colProps}>
                    <input
                      type="button"
                      value="view chart"
                      onClick={() => props.onCreateChart(soloData)}
                    />
                  </Cell>
                );
              }}
              width={100}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Date</Cell>}
          >
            <Column
              header=""
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                return (
                  <Cell {...colProps}>
                    {moment(soloData.startDt).format("ll")}
                  </Cell>
                );
              }}
              width={110}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={
              <Cell style={styles.groupHeaderStyle}>Form attributes</Cell>
            }
          >
            <Column
              header="survey"
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const form = props.soloMatchedForms[soloData.soloCastPk];
                if (!form || !form.survey) return <Cell {...colProps}>-</Cell>;
                return (
                  <Cell {...colProps} title={form.survey}>
                    {form.survey}
                  </Cell>
                );
              }}
              width={90}
              flexGrow={1}
            />
            <Column
              header="site"
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const form = props.soloMatchedForms[soloData.soloCastPk];
                if (!form || !form.siteId) return <Cell {...colProps}>-</Cell>;
                const { siteId } = form;
                return (
                  <Cell {...colProps} title={siteId}>
                    {siteId}
                  </Cell>
                );
              }}
              width={90}
              flexGrow={1}
            />
            <Column
              header="drop"
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const form = props.soloMatchedForms[soloData.soloCastPk];
                if (!form || !form.drop) return <Cell {...colProps}>-</Cell>;
                const { drop } = form;
                return (
                  <Cell {...colProps} title={drop}>
                    {drop}
                  </Cell>
                );
              }}
              width={90}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Serial</Cell>}
          >
            <Column
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const soloNum = getSoloPhysNum(
                  soloData.deviceSn,
                  props.soloUIDLookup
                );
                return (
                  <Cell {...colProps} title={soloNum}>
                    {soloNum}
                  </Cell>
                );
              }}
              width={70}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Form depth</Cell>}
          >
            <Column
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const form = props.soloMatchedForms[soloData.soloCastPk];
                if (!form || !form.targetDepthM)
                  return <Cell {...colProps}>-</Cell>;
                const depth = `${form.targetDepthM} m`;
                const style = getDepthCellStyle(
                  soloData.bottomDepth,
                  form.targetDepthM,
                  rowIndex
                );
                return (
                  <Cell {...colProps} style={style}>
                    {depth}
                  </Cell>
                );
              }}
              width={100}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={
              <Cell style={styles.groupHeaderStyle}>Instrument depth</Cell>
            }
          >
            <Column
              header="surface"
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const startDepth = soloData.startDepth
                  ? `${Math.round(soloData.startDepth * 10) / 10} m`
                  : "-";
                return (
                  <Cell {...colProps} title={startDepth}>
                    {startDepth}
                  </Cell>
                );
              }}
              width={90}
              flexGrow={1}
            />
            <Column
              header="bottom"
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const form = props.soloMatchedForms[soloData.soloCastPk];
                const bottomDepth = soloData.bottomDepth
                  ? `${Math.round(soloData.bottomDepth * 10) / 10} m`
                  : "-";
                const style = getDepthCellStyle(
                  soloData.bottomDepth,
                  form.targetDepthM,
                  rowIndex
                );
                return (
                  <Cell {...colProps} style={style} title={bottomDepth}>
                    {bottomDepth}
                  </Cell>
                );
              }}
              width={90}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Messenger</Cell>}
          >
            <Column
              header="drop time"
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const form = props.soloMatchedForms[soloData.soloCastPk];
                if (!form || !form.collected)
                  return <Cell {...colProps}>-</Cell>;
                const dt = moment(form.collected);
                const style = getTimeCellStyle(
                  soloData.bottomDt,
                  form.collected,
                  rowIndex
                );
                return (
                  <Cell {...colProps} style={style} title={dt.format()}>
                    {dt.format("LT")}
                  </Cell>
                );
              }}
              width={90}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={
              <Cell style={styles.groupHeaderStyle}>Instrument time</Cell>
            }
          >
            <Column
              header="start"
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const dt = moment(soloData.startDt);
                return (
                  <Cell {...colProps} title={dt.format()}>
                    {dt.format("LT")}
                  </Cell>
                );
              }}
              width={77}
              flexGrow={1}
            />
            <Column
              header="bottom"
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const form = props.soloMatchedForms[soloData.soloCastPk];
                const dt = moment(soloData.bottomDt);
                const style = getTimeCellStyle(
                  soloData.bottomDt,
                  form.collected,
                  rowIndex
                );
                return (
                  <Cell {...colProps} style={style} title={dt.format()}>
                    {dt.format("LT")}
                  </Cell>
                );
              }}
              width={77}
              flexGrow={1}
            />
            <Column
              header="end"
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const dt = moment(soloData.endDt);
                return (
                  <Cell {...colProps} title={dt.format()}>
                    {dt.format("LT")}
                  </Cell>
                );
              }}
              width={77}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Edit cast</Cell>}
          >
            <Column
              header="miscast"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const { miscast } = data;
                return (
                  <Cell {...colProps}>
                    <input
                      type="checkbox"
                      checked={miscast}
                      onChange={() =>
                        props.onCastMiscastCheck(
                          data.soloCastPk,
                          data.soloFilePk,
                          !miscast
                        )
                      }
                    />
                  </Cell>
                );
              }}
              width={70}
              flexGrow={1}
            />
            <Column
              header="add comment"
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                const { comments } = soloData;
                return (
                  <Cell {...colProps}>
                    <input
                      type="text"
                      style={styles.textInputStyle}
                      value={comments || ""}
                      onChange={e =>
                        props.onCastCommentsChange(
                          soloData.soloCastPk,
                          soloData.soloFilePk,
                          e.target.value
                        )
                      }
                    />
                  </Cell>
                );
              }}
              width={200}
              flexGrow={2}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Filename</Cell>}
          >
            <Column
              cell={({ rowIndex, ...colProps }) => {
                const soloData =
                  props.soloCastsInFilesBeingAnnotatedList[rowIndex];
                return <Cell {...colProps}>{soloData.filename}</Cell>;
              }}
              width={200}
              flexGrow={1}
            />
          </ColumnGroup>
        </Table>
      </div>
    );
  }
  return null;
};

const formShape = PropTypes.shape({
  collected: PropTypes.string,
  targetDepthM: PropTypes.number,
  siteId: PropTypes.string,
  drop: PropTypes.number,
  survey: PropTypes.string
});

const castShape = PropTypes.shape({
  soloCastPk: PropTypes.number.isRequired,
  soloFilePk: PropTypes.number.isRequired,
  startDt: PropTypes.string.isRequired,
  bottomDt: PropTypes.string.isRequired,
  endDt: PropTypes.string.isRequired,
  miscast: PropTypes.bool.isRequired,
  comments: PropTypes.string,
  startDepth: PropTypes.number.isRequired,
  bottomDepth: PropTypes.number.isRequired,
  filename: PropTypes.string,
  deviceSn: PropTypes.string
});

CastsInFileCard.propTypes = {
  width: PropTypes.number,
  soloCastsInFilesBeingAnnotatedList: PropTypes.arrayOf(castShape),
  soloDuplicateFormsInCastsBeingAnnotated: PropTypes.arrayOf(PropTypes.number),
  possibleForms: PropTypes.objectOf(PropTypes.arrayOf(formShape)),
  soloMatchedForms: PropTypes.objectOf(formShape),
  onCastChangeFormClick: PropTypes.func.isRequired,
  onCastMiscastCheck: PropTypes.func.isRequired,
  onCastCommentsChange: PropTypes.func.isRequired,
  onCreateChart: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  soloUIDLookup: PropTypes.object.isRequired
};

CastsInFileCard.defaultProps = {
  width: 900,
  soloCastsInFilesBeingAnnotatedList: [],
  soloDuplicateFormsInCastsBeingAnnotated: [],
  possibleForms: {},
  soloMatchedForms: {}
};

export default CastsInFileCard;
